import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { config } from "config";
import ToasterService from "utils/toaster.util";
import "./PaymentForm.scss";

const stripeElementOptions = {
  style: {
    base: {
      fontSize: "15px",
      fontWeight: "500",
      color: "#1a0f20",
      iconColor: "#58007d",
      fontSmoothing: "antialiased",
      fontFamily:
        "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
      "::placeholder": { color: "#b8b2bc" },
    },
    invalid: {
      iconColor: "#b72136",
      color: "#b72136",
    },
  },
};

const ShieldCheckIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3.25 18.75 6v5.1c0 4.55-3.2 7.75-6.75 9.15-3.55-1.4-6.75-4.6-6.75-9.15V6L12 3.25Z" />
    <path d="M9 12.15l2.1 2.1L15.3 9.9" />
  </svg>
);

type PartnerStripeCheckoutProps = {
  clientSecret: string;
  onSuccess: (paymentIntentId?: string) => void;
};

type StripeFieldKey = "number" | "expiry" | "cvc";

function StripePaymentForm({
  clientSecret,
  onSuccess,
}: {
  clientSecret: string;
  onSuccess: (paymentIntentId?: string) => void;
}) {
  const { t } = useTranslation("translation");
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<StripeFieldKey, string>>({
    number: "",
    expiry: "",
    cvc: "",
  });

  const handleFieldChange = (field: StripeFieldKey) => (event: any) => {
    setError("");
    setFieldErrors((prev) => ({ ...prev, [field]: event?.error?.message || "" }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) return;

    setSubmitting(true);
    setError("");
    try {
      const { trackPaymentStarted } = await import("services/mixpanel.service");
      trackPaymentStarted("card");
    } catch {
      /* analytics must never block payment */
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardNumberElement,
        },
      }
    );

    if (stripeError) {
      const message =
        stripeError.message ||
        String(t("paymentFailed", "Payment failed"));
      setError(message);
      ToasterService.showError(message);
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "requires_capture") {
      onSuccess(paymentIntent.id);
      return;
    }

    setError(String(t("paymentFailed", "Payment failed")));
    setSubmitting(false);
  };

  const combinedError = error || fieldErrors.number || fieldErrors.expiry || fieldErrors.cvc;

  return (
    <form onSubmit={onSubmit} className="yp-payment__stripe-form yp-payment__card">
      <div className="yp-payment__field">
        <span className="yp-payment__field-label">{t("cardDetails", "Card details")}</span>
        <div
          className={
            combinedError
              ? "yp-payment__card-field has-error"
              : "yp-payment__card-field"
          }
        >
          <div className="yp-payment__card-field-row yp-payment__card-field-number">
            <CardNumberElement
              options={{ ...stripeElementOptions, showIcon: true }}
              onChange={handleFieldChange("number")}
            />
          </div>
          <div className="yp-payment__card-field-row yp-payment__card-field-split">
            <div className="yp-payment__card-field-cell">
              <CardExpiryElement
                options={stripeElementOptions}
                onChange={handleFieldChange("expiry")}
              />
            </div>
            <span className="yp-payment__card-field-divider" aria-hidden="true" />
            <div className="yp-payment__card-field-cell">
              <CardCvcElement
                options={stripeElementOptions}
                onChange={handleFieldChange("cvc")}
              />
            </div>
          </div>
        </div>
        {combinedError ? <p className="yp-payment__field-error">{combinedError}</p> : null}
      </div>
      <button
        type="submit"
        className="yp-payment__submit"
        disabled={submitting}
        aria-busy={submitting || undefined}
      >
        {submitting ? (
          <span className="yp-payment__submit-loading">
            {(t("processing", "Processing...") || "Processing...").replace(/\.+$/, "")}
            <span className="yp-payment__submit-dots" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </span>
        ) : (
          t("payNow", "Pay now")
        )}
      </button>
      <span className="yp-payment__secure-note">
        <ShieldCheckIcon />
        <bdi>
          {t("securePayment", "Payments are encrypted and securely processed by Stripe")}
        </bdi>
      </span>
    </form>
  );
}

export default function PartnerStripeCheckout({
  clientSecret,
  onSuccess,
}: PartnerStripeCheckoutProps) {
  const stripePromise = loadStripe(config.STRIPE_KEY);

  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm clientSecret={clientSecret} onSuccess={onSuccess} />
    </Elements>
  );
}
